import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
    BellRing,
    Plus,
    X,
    Loader2,
    ExternalLink,
    Trash2,
    RefreshCw,
    Search,
    AlertTriangle,
    CheckCheck,
    Users,
} from "lucide-react";

const COURT_OPTIONS = ["CJEU", "ECtHR", "General Court", "EFTA Court", "National courts"];
const REGULATION_OPTIONS = [
    "GDPR", "AI Act", "DORA", "NIS2", "DSA", "DMA", "Data Act", "Data Governance Act",
    "MiCA", "CSRD", "CSDDD", "ePrivacy", "EHDS", "EUDR", "CBAM", "MiFID II",
    "Battery Regulation", "Cyber Resilience Act", "Product Liability Directive",
];
const SEVERITY_BADGE = {
    info: { label: "Info", cls: "bg-sky-50 text-sky-800 border-sky-200" },
    moderate: { label: "Moderate", cls: "bg-amber-50 text-amber-900 border-amber-200" },
    high: { label: "High", cls: "bg-rose-50 text-rose-900 border-rose-200" },
};

export default function WatchdogsAlerts() {
    const [tab, setTab] = useState("alerts");
    const [watches, setWatches] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const [{ data: wd }, { data: ad }] = await Promise.all([
                api.get("/watches"),
                api.get("/alerts?limit=80"),
            ]);
            setWatches(wd.items || []);
            setAlerts(ad.items || []);
            setUnread(ad.unread_count || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
    }, []);

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="watchdogs-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Module</div>
                    <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">
                        Watchdogs &amp; Live Alerts
                    </h1>
                    <p className="mt-3 max-w-2xl text-[#0D1B3E]/75">
                        Track regulations, courts, and topics. EuroLaw Hub scans for
                        material developments and files them in your alert centre with
                        editorial summaries and primary-source citations.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                        setEditing(null);
                        setShowCreate(true);
                    }}
                    data-testid="watchdogs-new-watch-btn"
                >
                    <Plus size={16} /> New watch
                </button>
            </div>

            <div className="mt-8 inline-flex border border-[#0D1B3E]/20 bg-white" data-testid="watchdogs-tabs">
                <Tab active={tab === "alerts"} onClick={() => setTab("alerts")} testId="watchdogs-tab-alerts">
                    Alert centre
                    {unread > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[#C9A84C] text-[#0D1B3E] text-[11px] font-semibold rounded-sm">
                            {unread}
                        </span>
                    )}
                </Tab>
                <Tab active={tab === "watches"} onClick={() => setTab("watches")} testId="watchdogs-tab-watches">
                    My watches ({watches.length})
                </Tab>
            </div>

            {loading ? (
                <div className="mt-10 flex items-center gap-3 text-[#0D1B3E]/60">
                    <Loader2 className="animate-spin" size={18} />
                    <span className="label-eyebrow">Loading…</span>
                </div>
            ) : tab === "alerts" ? (
                <AlertCentre
                    alerts={alerts}
                    unread={unread}
                    onRefresh={refresh}
                    setAlerts={setAlerts}
                    setUnread={setUnread}
                    onNewWatch={() => {
                        setEditing(null);
                        setShowCreate(true);
                    }}
                />
            ) : (
                <WatchList
                    watches={watches}
                    onRefresh={refresh}
                    onEdit={(w) => {
                        setEditing(w);
                        setShowCreate(true);
                    }}
                />
            )}

            {showCreate && (
                <WatchEditor
                    initial={editing}
                    onClose={() => setShowCreate(false)}
                    onSaved={async () => {
                        setShowCreate(false);
                        await refresh();
                    }}
                />
            )}
        </div>
    );
}

function Tab({ active, onClick, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`px-5 py-2.5 text-sm uppercase tracking-[0.18em] border-r last:border-r-0 border-[#0D1B3E]/20 transition-colors ${
                active
                    ? "bg-[#0D1B3E] text-[#F5F0E8]"
                    : "bg-transparent text-[#0D1B3E]/75 hover:text-[#0D1B3E]"
            }`}
        >
            {children}
        </button>
    );
}

function AlertCentre({ alerts, unread, onRefresh, setAlerts, setUnread, onNewWatch }) {
    const [busy, setBusy] = useState(false);

    const markRead = async (id) => {
        await api.post(`/alerts/${id}/read`);
        setAlerts((cur) => cur.map((a) => (a.id === id ? { ...a, read: true } : a)));
        setUnread((u) => Math.max(0, u - 1));
    };
    const dismiss = async (id) => {
        await api.delete(`/alerts/${id}`);
        const wasUnread = alerts.find((a) => a.id === id && !a.read);
        setAlerts((cur) => cur.filter((a) => a.id !== id));
        if (wasUnread) setUnread((u) => Math.max(0, u - 1));
    };
    const markAllRead = async () => {
        setBusy(true);
        try {
            await api.post("/alerts/mark-all-read");
            setAlerts((cur) => cur.map((a) => ({ ...a, read: true })));
            setUnread(0);
        } finally {
            setBusy(false);
        }
    };

    if (alerts.length === 0) {
        return (
            <div className="mt-12 card-flat p-12 text-center" data-testid="watchdogs-empty">
                <BellRing size={28} color="#C9A84C" className="mx-auto" />
                <div className="mt-4 font-display text-2xl tracking-tight">No alerts yet</div>
                <p className="mt-3 max-w-md mx-auto text-sm text-[#0D1B3E]/70">
                    Create a watch and run a scan. EuroLaw Hub will surface the most
                    material EU legal developments matching your topic, with primary
                    citations.
                </p>
                <button
                    type="button"
                    className="btn-primary mt-6"
                    onClick={onNewWatch}
                    data-testid="watchdogs-empty-cta"
                >
                    <Plus size={16} /> Create a watch
                </button>
            </div>
        );
    }

    return (
        <div className="mt-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-[#0D1B3E]/70">
                    {unread > 0
                        ? `${unread} unread alert${unread === 1 ? "" : "s"}`
                        : "All alerts read"}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onRefresh}
                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C] flex items-center gap-1.5"
                        data-testid="watchdogs-refresh-btn"
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button
                        type="button"
                        onClick={markAllRead}
                        disabled={busy || unread === 0}
                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                        data-testid="watchdogs-mark-all-read"
                    >
                        <CheckCheck size={14} /> Mark all read
                    </button>
                </div>
            </div>

            <ul className="mt-5 space-y-3" data-testid="watchdogs-alerts-list">
                {alerts.map((a) => (
                    <li
                        key={a.id}
                        className={`card-flat p-6 ${a.read ? "" : "border-[#C9A84C] border-l-4"}`}
                        data-testid={`alert-row-${a.id}`}
                    >
                        <div className="flex items-start gap-4 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap text-[11px] uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                                    <span>{a.watch_name || "Watch"}</span>
                                    {a.source_label && <span>· {a.source_label}</span>}
                                    {a.occurred_at && <span>· {a.occurred_at}</span>}
                                    <SeverityBadge severity={a.severity} />
                                    {!a.read && (
                                        <span className="text-[#C9A84C] font-semibold">· Unread</span>
                                    )}
                                </div>
                                <div className="mt-2 font-display text-xl tracking-tight">
                                    {a.title}
                                </div>
                                {a.summary && (
                                    <p className="mt-2 text-sm text-[#0D1B3E]/80 leading-relaxed">
                                        {a.summary}
                                    </p>
                                )}
                                {Array.isArray(a.tags) && a.tags.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {a.tags.map((t) => (
                                            <span
                                                key={t}
                                                className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 border border-[#0D1B3E]/20 text-[#0D1B3E]/70"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                                {a.source_url && (
                                    <a
                                        href={a.source_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C] flex items-center gap-1.5"
                                        data-testid={`alert-source-${a.id}`}
                                    >
                                        Source <ExternalLink size={13} />
                                    </a>
                                )}
                                {!a.read && (
                                    <button
                                        type="button"
                                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                                        onClick={() => markRead(a.id)}
                                        data-testid={`alert-mark-read-${a.id}`}
                                    >
                                        Mark read
                                    </button>
                                )}
                                {a.read && (
                                    <span
                                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/40"
                                        data-testid={`alert-mark-read-${a.id}`}
                                    >
                                        Read
                                    </span>
                                )}
                                <button
                                    type="button"
                                    aria-label="Dismiss"
                                    className="text-[#0D1B3E]/60 hover:text-rose-600"
                                    onClick={() => dismiss(a.id)}
                                    data-testid={`alert-dismiss-${a.id}`}
                                >
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function SeverityBadge({ severity }) {
    const s = SEVERITY_BADGE[severity] || SEVERITY_BADGE.info;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-semibold uppercase tracking-[0.16em] ${s.cls}`}>
            {severity === "high" && <AlertTriangle size={11} />}
            {s.label}
        </span>
    );
}

function WatchList({ watches, onRefresh, onEdit }) {
    const { user } = useAuth();
    const [scanning, setScanning] = useState({});

    const toggleShare = async (w) => {
        try {
            await api.put(`/watches/${w.id}`, { shared: !w.shared });
            await onRefresh();
        } catch {
            window.alert("Could not update sharing.");
        }
    };

    const scan = async (id) => {
        setScanning((s) => ({ ...s, [id]: true }));
        try {
            await api.post(`/watches/${id}/scan`);
            // Poll a few times for status to update
            for (let i = 0; i < 40; i++) {
                await new Promise((r) => setTimeout(r, 2500));
                const { data } = await api.get("/watches");
                const found = (data.items || []).find((w) => w.id === id);
                if (found && found.last_scan_status !== "running") {
                    break;
                }
            }
            await onRefresh();
        } catch (e) {
            const d = e?.response?.data?.detail;
            window.alert(typeof d === "string" ? d : d?.message || "Scan failed.");
        } finally {
            setScanning((s) => ({ ...s, [id]: false }));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this watch and its alerts?")) return;
        await api.delete(`/watches/${id}`);
        await onRefresh();
    };

    if (watches.length === 0) {
        return (
            <div className="mt-12 card-flat p-12 text-center" data-testid="watchdogs-no-watches">
                <Search size={28} color="#C9A84C" className="mx-auto" />
                <div className="mt-4 font-display text-2xl tracking-tight">
                    You have no watches yet
                </div>
                <p className="mt-3 max-w-md mx-auto text-sm text-[#0D1B3E]/70">
                    Create your first watch above. Each scan costs 1 credit.
                </p>
            </div>
        );
    }

    return (
        <ul className="mt-8 grid gap-4 md:grid-cols-2" data-testid="watchdogs-watch-list">
            {watches.map((w) => {
                const isScan = scanning[w.id] || w.last_scan_status === "running";
                return (
                    <li
                        key={w.id}
                        className="card-flat p-6"
                        data-testid={`watch-card-${w.id}`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="label-eyebrow">
                                    {w.scan_frequency} · {w.active ? "Active" : "Paused"}
                                    {w.shared && " · Shared"}
                                </div>
                                {w.owned === false && (
                                    <span className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#7a6222]" data-testid={`watch-shared-by-${w.id}`}>
                                        <Users size={11} /> Shared by {w.owner_name || "teammate"}
                                    </span>
                                )}
                                <div className="mt-2 font-display text-xl tracking-tight">
                                    {w.name}
                                </div>
                                {w.description && (
                                    <p className="mt-2 text-sm text-[#0D1B3E]/75">
                                        {w.description}
                                    </p>
                                )}
                            </div>
                            <div className="text-right text-xs text-[#0D1B3E]/55">
                                <div className="font-semibold text-[#0D1B3E]">
                                    {w.unread_count} unread
                                </div>
                                <div>{w.alert_count} total</div>
                            </div>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-1.5">
                            {(w.regulations || []).map((r) => (
                                <span
                                    key={`reg-${r}`}
                                    className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 bg-[#0D1B3E] text-[#F5F0E8]"
                                >
                                    {r}
                                </span>
                            ))}
                            {(w.courts || []).map((c) => (
                                <span
                                    key={`court-${c}`}
                                    className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 border border-[#0D1B3E]/20 text-[#0D1B3E]/70"
                                >
                                    {c}
                                </span>
                            ))}
                            {(w.keywords || []).map((k) => (
                                <span
                                    key={`kw-${k}`}
                                    className="text-[10px] tracking-[0.04em] px-2 py-0.5 border border-[#C9A84C]/40 text-[#0D1B3E]/80"
                                >
                                    #{k}
                                </span>
                            ))}
                        </div>
                        {w.last_scan_status === "failed" && (
                            <p className="mt-3 text-xs text-rose-700">
                                Last scan failed: {w.last_scan_error}
                            </p>
                        )}
                        {w.last_scanned_at && (
                            <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#0D1B3E]/50">
                                Last scan {new Date(w.last_scanned_at).toLocaleString()}
                            </p>
                        )}
                        <div className="mt-5 flex items-center gap-3 flex-wrap">
                            {w.owned === false ? (
                                <span className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/50" data-testid={`watch-readonly-${w.id}`}>
                                    Read-only · managed by {w.owner_name || "teammate"}
                                </span>
                            ) : (
                            <>
                            <button
                                type="button"
                                className="btn-primary"
                                onClick={() => scan(w.id)}
                                disabled={isScan}
                                data-testid={`watch-scan-${w.id}`}
                            >
                                {isScan ? (
                                    <>
                                        <Loader2 className="animate-spin" size={14} /> Scanning…
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw size={14} /> Scan now (1 credit)
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                                onClick={() => onEdit(w)}
                                data-testid={`watch-edit-${w.id}`}
                            >
                                Edit
                            </button>
                            <button
                                type="button"
                                className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-rose-600"
                                onClick={() => remove(w.id)}
                                data-testid={`watch-delete-${w.id}`}
                            >
                                Delete
                            </button>
                            {user?.team_id && (
                                <button
                                    type="button"
                                    className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C] inline-flex items-center gap-1"
                                    onClick={() => toggleShare(w)}
                                    data-testid={`watch-share-${w.id}`}
                                >
                                    <Users size={12} /> {w.shared ? "Unshare" : "Share with team"}
                                </button>
                            )}
                            </>
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}

function WatchEditor({ initial, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: initial?.name || "",
        description: initial?.description || "",
        keywords: (initial?.keywords || []).join(", "),
        regulations: initial?.regulations || [],
        courts: initial?.courts || [],
        scan_frequency: initial?.scan_frequency || "manual",
        active: initial?.active ?? true,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const toggleArr = (key, value) =>
        setForm((f) => {
            const cur = new Set(f[key]);
            if (cur.has(value)) cur.delete(value);
            else cur.add(value);
            return { ...f, [key]: Array.from(cur) };
        });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                keywords: form.keywords
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                regulations: form.regulations,
                courts: form.courts,
                scan_frequency: form.scan_frequency,
                active: form.active,
            };
            if (initial) {
                await api.put(`/watches/${initial.id}`, payload);
            } else {
                await api.post("/watches", payload);
            }
            await onSaved();
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            setError(typeof d === "string" ? d : d?.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-[#0D1B3E]/60 grid place-items-center p-4"
            onClick={onClose}
            data-testid="watch-editor-overlay"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-white border border-[#0D1B3E]/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-7"
                data-testid="watch-editor"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <div className="label-eyebrow">{initial ? "Edit watch" : "New watch"}</div>
                        <h2 className="font-display text-2xl mt-1 tracking-tight">
                            {initial ? form.name : "Create a watchdog"}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[#0D1B3E]/60 hover:text-[#0D1B3E]"
                        data-testid="watch-editor-close"
                    >
                        <X size={22} />
                    </button>
                </div>

                <div className="mt-6 space-y-5">
                    <div>
                        <label className="label-eyebrow">Name</label>
                        <input
                            type="text"
                            className="field mt-2"
                            placeholder="e.g. AI Act: high-risk obligations"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            data-testid="watch-name-input"
                        />
                    </div>
                    <div>
                        <label className="label-eyebrow">Topic / description</label>
                        <textarea
                            className="field mt-2"
                            rows={3}
                            placeholder="What developments should EuroLaw Hub surface?"
                            value={form.description}
                            onChange={(e) =>
                                setForm({ ...form, description: e.target.value })
                            }
                            data-testid="watch-description-input"
                        />
                    </div>

                    <div>
                        <label className="label-eyebrow">Regulations to track</label>
                        <div className="mt-2 flex flex-wrap gap-2" data-testid="watch-regulations-chips">
                            {REGULATION_OPTIONS.map((r) => {
                                const active = form.regulations.includes(r);
                                return (
                                    <button
                                        key={r}
                                        type="button"
                                        onClick={() => toggleArr("regulations", r)}
                                        className={`text-xs uppercase tracking-[0.16em] px-2.5 py-1 border transition-colors ${
                                            active
                                                ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                                : "bg-transparent text-[#0D1B3E]/75 border-[#0D1B3E]/25 hover:border-[#0D1B3E]"
                                        }`}
                                        data-testid={`watch-reg-${r}`}
                                    >
                                        {r}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="label-eyebrow">Courts</label>
                        <div className="mt-2 flex flex-wrap gap-2" data-testid="watch-courts-chips">
                            {COURT_OPTIONS.map((c) => {
                                const active = form.courts.includes(c);
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => toggleArr("courts", c)}
                                        className={`text-xs uppercase tracking-[0.16em] px-2.5 py-1 border transition-colors ${
                                            active
                                                ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                                : "bg-transparent text-[#0D1B3E]/75 border-[#0D1B3E]/25 hover:border-[#0D1B3E]"
                                        }`}
                                        data-testid={`watch-court-${c}`}
                                    >
                                        {c}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="label-eyebrow">Keywords (comma-separated)</label>
                        <input
                            type="text"
                            className="field mt-2"
                            placeholder="e.g. SCC, transfer impact assessment, government access"
                            value={form.keywords}
                            onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                            data-testid="watch-keywords-input"
                        />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label-eyebrow">Frequency</label>
                            <select
                                className="field mt-2"
                                value={form.scan_frequency}
                                onChange={(e) =>
                                    setForm({ ...form, scan_frequency: e.target.value })
                                }
                                data-testid="watch-frequency-select"
                            >
                                <option value="manual">Manual</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.active}
                                    onChange={(e) =>
                                        setForm({ ...form, active: e.target.checked })
                                    }
                                    data-testid="watch-active-toggle"
                                />
                                <span>Active</span>
                            </label>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mt-4 text-sm text-rose-700" data-testid="watch-editor-error">
                        {error}
                    </div>
                )}

                <div className="mt-7 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary"
                        data-testid="watch-editor-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={saving || !form.name.trim()}
                        data-testid="watch-editor-save"
                    >
                        {saving ? <Loader2 className="animate-spin" size={14} /> : null}
                        {initial ? "Save" : "Create watch"}
                    </button>
                </div>
            </form>
        </div>
    );
}
